import {
    ActionPerformed,
    PushNotifications as CapacitorPushNotifications,
    Channel,
    PushNotificationSchema,
    Token,
} from "@capacitor/push-notifications";
import { FirebaseApp, initializeApp } from "firebase/app";
import {
    Messaging as FirebaseMessaging,
    getMessaging as getMessagingFg,
    getToken,
    MessagePayload,
    onMessage,
} from "firebase/messaging";
import { NotificationApi } from "../api/api/notification-api";
import {
    BfDevice,
    BfNotificationRequest,
    BfNotificationSound,
    BfRegisterDevice,
    BfUser,
} from "../api/models";
import { BfMessageFcmData } from "../api/models/bf-message-fcm-data";
import { DEBUG_MODE, getServiceWorker, isAndroidApp, isCapacitor, isWebKit } from "../client";
import { Friendly } from "../friendly";
import { Toast } from "../toast";
import { Urls } from "../urls";
import {
    FirebaseInstanceType,
    isNotificationTypeSupported,
    MessagingOptions,
    showNotificationWithServiceWorker,
} from "./common";

// Be warned, here be dragons!!
// Initialise Firebase messaging. This class aims to hide some of the complexity, but it's really fragmented.

// For everything (everything except iOS/Capacitor) firebase has:
// - A foreground instance, for when the app is foregrounded.
// - A background instance, for when the app is backgrounded/dead.
// A notification will arrive and be handled by either the foreground or background handler.
// OnClick is then handled either natively, or below. Usually through a call to window.something.

// For the Capacitor version, firebase has:
// - A special Capacitor Firebase instance.

// 1. Everything (foreground, service worker & capacitor) will call setupFirebase().
// 2. getToken() is called to get permission across all instances.
// 3a. onForegroundMessage is called when in Fg (except iOS/capacitor).
// 3b. onBackgroundMessage is called when in Bg (except iOS/capacitor).
// 3c. iOS/capacitor is handled natively. Nothing to do here.
// 4a. onClick is processed by an event handler in iosSetupCapacitor()
// 4b. If foregrounded, onClick will be handled natively by the service worker.
// 4c. If for some reason, the sw isn't happy. Notification() will be attempted (likely deprecated).
// 4d. Worst case a toast will be shown. Rare.

export class ForegroundMessaging {
    private static instanceType: FirebaseInstanceType = FirebaseInstanceType.Unsupported;
    private static firebaseApp?: FirebaseApp;
    private static firebaseMessaging?: FirebaseMessaging;
    private static serviceworkerRegistration?: ServiceWorkerRegistration;

    static setupFirebase(firebaseOptions: MessagingOptions) {
        // If Firebase is initialised on an unsupported platform, it will cause a black screen
        if (isCapacitor()) {
            // Setup Cordova for iOS. Supports both Foreground and Background
            ForegroundMessaging.instanceType = FirebaseInstanceType.Capacitor;
            ForegroundMessaging.setupCapacitor();
            return Promise.resolve(null);
        }

        // OSX doesn't support Firebase. But supports APN instead. Add this one day.
        if (isWebKit()) {
            return Promise.reject("unsupported platform");
        }

        // Determine the remaining instance type
        ForegroundMessaging.instanceType = FirebaseInstanceType.App;

        try {
            // Only initialise once
            if (!ForegroundMessaging.firebaseMessaging) {
                ForegroundMessaging.firebaseApp = initializeApp(firebaseOptions);
                ForegroundMessaging.firebaseMessaging = getMessagingFg(
                    ForegroundMessaging.firebaseApp
                );

                if (ForegroundMessaging.firebaseMessaging) {
                    ForegroundMessaging.appSetupForeground();

                    // Connect to the service worker, and ensure it's sane
                    // We don't care if it doesn't return
                    return ForegroundMessaging.appConnectToServiceWorker();
                }
            } else {
                return ForegroundMessaging.appConnectToServiceWorker();
            }
        } catch (ex) {
            console.error("Error initialising firebase 💥", ex);
        }

        return Promise.reject("unable to initialise firebase");
    }

    private static setupCapacitor() {
        // Show us the notification payload if the app is open on our device
        CapacitorPushNotifications.addListener(
            "pushNotificationReceived",
            (notification: PushNotificationSchema) => {
                if (DEBUG_MODE) console.log("pushNotificationReceived", notification);
            }
        );

        // Method called when tapping on a notification
        CapacitorPushNotifications.addListener(
            "pushNotificationActionPerformed",
            (notification: ActionPerformed) => {
                if (DEBUG_MODE)
                    console.log(
                        "pushNotificationActionPerformed",
                        JSON.stringify(notification.notification)
                    );

                if (notification.notification.data) {
                    const fcmMessage = notification.notification.data as BfMessageFcmData;

                    if (fcmMessage && fcmMessage.navigate) {
                        Urls.redirectToPathNormalise(fcmMessage.navigate);
                    }
                }
            }
        );
    }

    static async updateChannels(expected: BfNotificationRequest[]) {
        if (!isAndroidApp()) return;

        const defaultChannel = {
            notificationId: "default",
            name: "Default",
            sound: BfNotificationSound.default,
            critical: false,
        };

        const allExpected = [
            ...expected.filter((ec) => ec.notificationId !== "default"),
            defaultChannel,
        ];

        // Load current channels once
        let currentChannels: Channel[] = [];
        try {
            const res = await CapacitorPushNotifications.listChannels();
            currentChannels = res.channels || [];
        } catch (e) {
            console.warn("error listing channels", e);
        }

        const expectedById = new Map(allExpected.map((ec) => [ec.notificationId, ec]));
        const expectedIds = new Set(expectedById.keys());

        // 1) Delete channels that shouldn’t exist at all
        const deleteUnexpected = currentChannels
            .filter((ch) => !expectedIds.has(ch.id))
            .map((ch) => {
                if (DEBUG_MODE) {
                    console.log(
                        "ForegroundMessaging.updateChannels deleting unexpected",
                        ch.id,
                        ch
                    );
                }
                return CapacitorPushNotifications.deleteChannel({ id: ch.id }).catch((e) =>
                    console.warn("error deleting channel", ch.id, e)
                );
            });

        await Promise.all(deleteUnexpected);

        // 2) For each expected channel: create or recreate if needed
        for (const ec of allExpected) {
            if (!ec.name || !ec.notificationId) {
                console.warn("ForegroundMessaging.updateChannels skipping invalid", ec);
                continue;
            }

            const existing = currentChannels.find((ch) => ch.id === ec.notificationId);

            const desiredSound = Friendly.AndroidSound(ec.sound);
            const needsCreate = !existing;
            const needsRecreate =
                !!existing &&
                (existing.name !== ec.name ||
                    (desiredSound !== existing.sound &&
                        desiredSound &&
                        existing.sound?.endsWith(desiredSound)));

            if (needsCreate || needsRecreate) {
                if (needsRecreate) {
                    if (DEBUG_MODE) {
                        console.log(
                            "ForegroundMessaging.updateChannels recreating",
                            existing!.id,
                            JSON.stringify(existing)
                        );
                    }
                    try {
                        await CapacitorPushNotifications.deleteChannel({ id: existing!.id });
                    } catch (e) {
                        console.warn("error deleting channel", existing!.id, e);
                    }
                }

                if (DEBUG_MODE) {
                    console.log(
                        "ForegroundMessaging.updateChannels creating",
                        ec.notificationId,
                        ec.name
                    );
                }
                try {
                    if (desiredSound === "default") {
                        await CapacitorPushNotifications.createChannel({
                            id: ec.notificationId,
                            name: ec.name,
                            importance: ec.critical ? 5 : 3,
                            visibility: 1,
                        });
                    } else {
                        await CapacitorPushNotifications.createChannel({
                            id: ec.notificationId,
                            name: ec.name,
                            importance: ec.critical ? 5 : 3,
                            visibility: 1,
                            sound: desiredSound,
                        });
                    }
                } catch (e) {
                    console.warn("error creating channel", ec.notificationId, e);
                }
            }
        }
    }

    private static appSetupForeground() {
        // Sets up the Foreground App instance
        if (
            ForegroundMessaging.instanceType === FirebaseInstanceType.App &&
            ForegroundMessaging.firebaseMessaging
        ) {
            onMessage(
                ForegroundMessaging.firebaseMessaging,
                ForegroundMessaging.onForegroundMessage
            );
        }
    }

    static async appConnectToServiceWorker() {
        if (ForegroundMessaging.instanceType !== FirebaseInstanceType.App) {
            console.warn(
                "appConnectToServiceWorker, invalid instance type",
                ForegroundMessaging.instanceType
            );
            return Promise.reject("invalid instance type");
        }

        return getServiceWorker()
            .then((registration) => {
                ForegroundMessaging.updateServiceWorkerRegistration(registration);
                return registration;
            })
            .catch((e) => {
                console.warn("appConnectToServiceWorker, error while connecting", e);
                return null;
            });
    }

    static updateServiceWorkerRegistration(registration: ServiceWorkerRegistration) {
        ForegroundMessaging.serviceworkerRegistration = registration;
    }

    static onForegroundMessage(msg: MessagePayload) {
        // PC and Android, when running in fg
        console.log("onForegroundMessage", msg);

        const castMessage = (msg as unknown as any).data as BfMessageFcmData;

        if (!castMessage) {
            console.warn("message received was not a BfFcmMessage", msg);
            return;
        }

        // Prefer to use the service worker
        if (ForegroundMessaging.serviceworkerRegistration) {
            return showNotificationWithServiceWorker(
                castMessage,
                ForegroundMessaging.serviceworkerRegistration
            );
        }

        // Check we have permission to use Notification() (unlikely, it's deprecated)
        if (isNotificationTypeSupported()) {
            return ForegroundMessaging.showNotificationWithNotification(castMessage);
        }

        // Instead, assuming we have permission, show OS message
        return ForegroundMessaging.showNotificationAsToast(castMessage);
    }

    private static showNotificationAsToast(msg: BfMessageFcmData) {
        if (msg && msg.zoom && msg.lat && msg.lon && msg.ehash) {
            Toast.infoAction(
                msg.body,
                () => {
                    Urls.redirectToPathNormalise(
                        `/?zoom=${msg.zoom}&lat=${msg.zoom}&lon=${msg.zoom}&ehash=${msg.ehash}`
                    );
                },
                "View"
            );
        } else {
            Toast.info(msg.body);
        }

        return true;
    }

    private static showNotificationWithNotification(msg: BfMessageFcmData) {
        try {
            const notificationOptions: NotificationOptions = {
                body: msg.body,
                icon: msg.icon,
                badge: msg.badge,
            };

            const notification = new Notification(msg.title, notificationOptions);

            notification.onclick = function () {
                window.focus();

                if (msg && msg.navigate) {
                    window.open(msg.navigate, "_self");
                }

                notification.close();
            };
        } catch (e) {
            console.log("Unable to create via Notification", e);
        }
    }

    /// Keeps the FCM token in sync with the server
    /// Returns true if notifications are enabled
    static keepServerTokenInSync(
        user: BfUser,
        currentDevice: BfDevice,
        notificationsShouldBeActive: boolean,
        notificationApi: NotificationApi
    ): Promise<boolean> {
        // Check if we're logged in and notifications are supported
        if (!isNotificationTypeSupported() && !isCapacitor()) {
            console.log("keepServerTokenInSync, notifications not supported");
            return Promise.resolve(false);
        }

        // Determine if the notification permission has been granted
        if (notificationsShouldBeActive) {
            // Ship it to the server
            return ForegroundMessaging.updateTokenWithServer(user, currentDevice, notificationApi);
        } else {
            // Check if the device is already known by the server (as this hasn't always been checked and the flag set)
            return notificationApi
                .getDevice(currentDevice.deviceId)
                .then((data) => {
                    if (data.data) {
                        return ForegroundMessaging.updateTokenWithServer(
                            user,
                            currentDevice,
                            notificationApi
                        );
                    } else {
                        console.warn("keepServerTokenInSync, device not registered");
                        return false;
                    }
                })
                .catch(() => {
                    console.warn("keepServerTokenInSync, unable to complete get device");
                    return false;
                });
        }
    }

    // Fetch the current FCM token and update it on the server
    // Returns true if the token was updated
    static updateTokenWithServer(
        user: BfUser,
        currentDevice: BfDevice,
        notificationApi: NotificationApi
    ) {
        // Get the current token
        return ForegroundMessaging.getToken()
            .then((token) => {
                if (!user) {
                    throw new Error("no user");
                }

                // Prepare the update
                const newDevice: BfRegisterDevice = {
                    firebaseId: token as string,
                    device: currentDevice,
                    deviceId: currentDevice.deviceId,
                    userId: user.userId,
                };

                return notificationApi
                    .updateDeviceToken(currentDevice.deviceId, newDevice)
                    .then(() => {
                        return true;
                    })
                    .catch((err) => {
                        console.warn("keepServerTokenInSync error updating device token", err);
                        return false;
                    });
            })
            .catch((e) => {
                console.warn("keepServerTokenInSync error fetching token", e);
                return false;
            });
    }

    static async getToken() {
        // Handle App
        if (
            ForegroundMessaging.instanceType === FirebaseInstanceType.App &&
            ForegroundMessaging.firebaseMessaging &&
            "serviceWorker" in navigator
        ) {
            // Get the token, this will trigger a permission request

            return ForegroundMessaging.appConnectToServiceWorker().then((registration) => {
                if (ForegroundMessaging.firebaseMessaging && registration) {
                    return getToken(ForegroundMessaging.firebaseMessaging, {
                        serviceWorkerRegistration: registration,
                    });
                } else {
                    console.error(
                        "firebase messaging or registration not available",
                        registration,
                        ForegroundMessaging.firebaseMessaging
                    );
                    throw new Error("firebase messaging or registration not available");
                }
            });
        }

        // Handle Capacitor/ios
        if (ForegroundMessaging.instanceType === FirebaseInstanceType.Capacitor) {
            const tokenPromise = new Promise<string>(function (resolve, reject) {
                ForegroundMessaging.capacitorGetPermission()
                    .then((token) => {
                        if (token) {
                            resolve(token);
                        } else {
                            reject("notification request denied or not supported");
                        }
                    })
                    .catch((err) => {
                        reject(err);
                    });
            });

            return tokenPromise;
        }

        throw new Error("unsupported configuration triggered getToken()");
    }

    /// This makes it a bit easier to setup push notifications easier on iOS, providing a similar API
    private static capacitorRegister() {
        // Setup a promise to handle registration
        return new Promise<string>((resolve, reject) => {
            // Post registration result
            const pushRegistrationHandle = CapacitorPushNotifications.addListener(
                "registration",
                (token: Token) => {
                    console.log("capacitorRegister", token);
                    resolve(token.value);

                    pushRegistrationHandle
                        .then((plh) => {
                            plh.remove();
                        })
                        .catch((e) => {
                            console.warn("error pushRegistrationHandle", e);
                        });
                }
            );

            // Registration error
            const pushRegistrationErrorHandle = CapacitorPushNotifications.addListener(
                "registrationError",
                (err: any) => {
                    console.warn("registrationError", err);
                    reject(err);

                    // eslint-disable-next-line promise/no-promise-in-callback
                    pushRegistrationErrorHandle
                        .then((plh) => {
                            plh.remove();
                        })
                        .catch((e) => {
                            console.warn("error pushRegistrationHandle", e);
                        });
                }
            );

            CapacitorPushNotifications.register().catch((e) => {
                console.warn("error CapacitorPushNotifications.register()", e);
                reject(e);
            });
        });
    }

    private static async capacitorGetPermission() {
        const capacitorHasPermission = await CapacitorPushNotifications.checkPermissions();

        switch (capacitorHasPermission.receive) {
            case "granted": {
                return ForegroundMessaging.capacitorRegister();
            }
            case "prompt":
            case "prompt-with-rationale": {
                const capacitorGetPermission =
                    await CapacitorPushNotifications.requestPermissions();

                if (capacitorGetPermission.receive === "granted") {
                    return ForegroundMessaging.capacitorRegister();
                }
                // Otherwise it's denied or prompt again
                break;
            }
            case "denied": {
                break;
            }
        }

        return false;
    }
}
